import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Activity, AlertCircle, Loader2, Mic, MicOff, Send, Sparkles } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { useFacility } from '../context/FacilityContext';
import assistantService from '../services/assistantService';

// Recharts can't read CSS vars — pull --c-brand off the documentElement
// so chart strokes/fills track the brand axis (blue/green/orange). The
// MutationObserver watches data-brand changes and re-reads the var so
// charts re-render with the new hue without remounting.
const useBrandColor = () => {
  const [color, setColor] = useState('#2563eb');
  useEffect(() => {
    const read = () => {
      const v = getComputedStyle(document.documentElement)
        .getPropertyValue('--c-brand')
        .trim();
      if (v) setColor(v);
    };
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-brand', 'class'],
    });
    return () => obs.disconnect();
  }, []);
  return color;
};


// Predictive Intelligence lives here rather than on the camera surface
// (moved 2026-07-21): churn risk, maintenance and demand forecasting are
// ANSWERS ABOUT THE FACILITY, which is exactly what this page is for.
//
// It renders an explicit "Coming Soon" and fabricates NO scores — a roadmap
// surface, not a demo. Delete this component the day the models ship and
// replace it with the real answers.
const PredictivePanel = () => (
  <div className="mt-8 rounded-xl border border-slate-200/80 bg-white p-8 text-center">
    <Activity className="h-10 w-10 mx-auto mb-3 text-blue-600" />
    <h2 className="text-[15px] font-semibold text-slate-900">
      Predictive Intelligence
    </h2>
    <p className="mt-2 text-[13px] text-slate-500 max-w-md mx-auto">
      Churn risk scoring, predictive maintenance, and demand forecasting will
      surface here once the scoring models ship. Operators will see at-risk
      tenants, units due for service, and occupancy-driven pricing signals
      alongside the answers above.
    </p>
    <span className="mt-4 inline-block text-[11px] font-medium text-slate-400 uppercase tracking-wider">
      Coming Soon
    </span>
  </div>
);

const Assistant = () => {
  const { facilityId, currentFacility } = useFacility();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [examples, setExamples] = useState([]);
  const [history, setHistory] = useState([]);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const brandColor = useBrandColor();

  const loadIntents = useCallback(async () => {
    if (!facilityId) return;
    try {
      const res = await assistantService.getIntents(facilityId);
      setExamples(res.data?.example_prompts || []);
    } catch (err) {
      console.error('Failed to load assistant intents:', err);
    }
  }, [facilityId]);

  useEffect(() => {
    loadIntents();
  }, [loadIntents]);

  const runQuery = async (q) => {
    if (!facilityId || !q.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await assistantService.runQuery(facilityId, q.trim());
      setResult(res.data);
      setHistory((prev) => [{ query: q.trim(), response: res.data }, ...prev].slice(0, 10));
    } catch (err) {
      console.error('Assistant query failed:', err);
      setError(err.response?.data?.detail || 'Unable to run that query.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runQuery(query);
  };

  const handleExample = (prompt) => {
    setQuery(prompt);
    runQuery(prompt);
  };

  const toggleVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice input is not supported in this browser.');
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setQuery(transcript);
      runQuery(transcript);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognition.start();
    recognitionRef.current = recognition;
    setListening(true);
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-blue-600" />
          <h1 className="text-xl font-semibold text-slate-900">AI Facility Chatbot</h1>
        </div>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Ask about {currentFacility?.name || 'this facility'} in plain English.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mb-4">
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={listening ? 'Listening...' : 'e.g. Which tenants are past due?'}
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-24 text-[14px] text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            disabled={loading}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <button
              type="button"
              onClick={toggleVoice}
              className={`p-2 rounded-lg transition-colors ${
                listening
                  ? 'bg-red-100 text-red-600 animate-pulse'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
              title={listening ? 'Stop listening' : 'Voice input'}
            >
              {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="p-2 rounded-lg bg-blue-600 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </form>

      {examples.length > 0 && !result && !loading && (
        <div className="mb-6">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Try asking
          </p>
          <div className="flex flex-wrap gap-2">
            {examples.map((prompt) => (
              <button
                key={prompt}
                onClick={() => handleExample(prompt)}
                className="px-3 py-1.5 rounded-full border border-slate-200 bg-white text-[12px] text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-[13px] text-red-600 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {result && <AssistantResult result={result} />}

      <PredictivePanel />

      {history.length > 1 && (
        <div className="mt-8">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Recent queries
          </p>
          <div className="space-y-1">
            {history.slice(1).map((item, idx) => (
              <button
                key={`${item.query}-${idx}`}
                onClick={() => handleExample(item.query)}
                className="block w-full text-left px-3 py-2 rounded-lg text-[12px] text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
              >
                {item.query}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AssistantResult = ({ result }) => {
  const { intent, confidence, answer, columns = [], rows = [], chart } = result;
  const isUnknown = intent === 'unknown';

  return (
    <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <p className="text-[14px] text-slate-800 font-medium">{answer}</p>
          {!isUnknown && (
            <span className="text-[10px] text-slate-400 font-mono">
              {intent} · {Math.round(confidence * 100)}%
            </span>
          )}
        </div>
      </div>

      {chart && rows.length > 0 && (
        <div className="px-5 py-4 border-b border-slate-100">
          <ResponsiveContainer width="100%" height={220}>
            {chart.type === 'line' ? (
              <LineChart data={rows.map((r) => ({ x: r[chart.x], y: r[chart.y] }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="x" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip />
                <Line type="monotone" dataKey="y" stroke={brandColor} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            ) : (
              <BarChart data={rows.map((r) => ({ x: r[chart.x], y: r[chart.y] }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="x" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip />
                <Bar dataKey="y" fill={brandColor} radius={[4, 4, 0, 0]} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {rows.length > 0 && columns.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col}
                    className="px-5 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={idx}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50"
                >
                  {row.map((cell, cellIdx) => (
                    <td key={cellIdx} className="px-5 py-2.5 text-slate-700">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length === 0 && !isUnknown && (
        <div className="px-5 py-6 text-center text-[13px] text-slate-400">
          No matching records.
        </div>
      )}
    </div>
  );
};

export default Assistant;
